import { useMemo, type ReactNode } from "react";
import {
  SelectionStyleParentPanel,
  type SelectionStyleChildKey,
  type SelectionStyleParentItem,
} from "./selection-style-parent-panel";
import {
  CONNECTOR_AUTO_ROUTE,
  resolveConnectorAppearance,
  type ConnectorCap,
  type ConnectorLinePattern,
  type ConnectorLineType,
  type StrokePattern,
  type TableCanvasObject,
  type TextAlign,
} from "@refinement-canvas/domain";
import {
  ConnectorCapChooser,
  ConnectorCapPreview,
  ConnectorLinePatternChooser,
  ConnectorLinePatternPreview,
  ConnectorLineTypeChooser,
  ConnectorLineTypePreview,
  ConnectorRouteActions,
} from "./connector-style-controls";
import { useConnectorEditingAvailable } from "../connector-editing-capability";
import {
  computeConnectorGeometry,
  connectorFrameOf,
  connectorGeometryInputFor,
} from "../../../lib/connector-geometry";
import { TableCellAuxiliaryInput, TableShapeActions } from "./table-shape-actions";
import { useTableCellEditorStore } from "../input/table-cell-editor-store";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { resolveSelectionStyleTarget, type SelectionStyleTarget } from "./selection-style-target";
import {
  ColorSwatchList,
  ColorValuePreview,
  PenThicknessChooser,
  ShapeStrokeWidthChooser,
  StrokePatternChooser,
  StrokePatternPreview,
  SubPanel,
} from "./toolbar-controls";
import {
  isTextStyleItemKey,
  renderTextStyleChild,
  textStyleAlignPreview,
  textStyleParentItems,
  TEXT_STYLE_ITEM_LABELS,
  type TextStyleChildInput,
} from "./text-style-panel-model";
import {
  isValidHexColor,
  normalizeHexColor,
  PEN_PRESET_COLORS,
  PEN_THICKNESS_PIXELS,
  savePenColor,
  type PenThickness,
} from "../../../lib/pen-preference";
import { saveStickyColor, STICKY_PRESET_COLORS } from "../../../lib/sticky-preference";
import {
  saveShapeFill,
  saveShapeStroke,
  SHAPE_FILL_NONE,
  SHAPE_FILL_PRESET_COLORS,
  SHAPE_FILL_TRANSPARENT,
  SHAPE_STROKE_NONE,
  SHAPE_STROKE_PRESET_COLORS,
  SHAPE_STROKE_TRANSPARENT,
  SHAPE_STROKE_WIDTH_PIXELS,
  type ShapeStrokeWidth,
} from "../../../lib/shape-preference";
import { SECTION_FILL_PRESET_COLORS } from "../../../lib/section-preference";
import type { CustomColorPalettes } from "./use-custom-color-palettes";
import {
  CONNECTOR_STROKE_PRESET_COLORS,
  CONNECTOR_STROKE_WIDTH_PIXELS,
} from "../../../lib/connector-style";
import { SelectionStylePanelFrame } from "./selection-style-panel-frame";

// 選択中オブジェクトのスタイルを変更するツールバーのサブパネル群。
// 「配置前に既定値を選ぶ UI」（tool-bar.tsx のツール別パネル）と同じ部品・同じ選択肢を使い、
// 利用者が設定項目を 2 か所で覚えなくて済むようにする。テキストについては、項目の順序・ラベル・
// プレビュー・子パネルの選択肢・太字ボタンを text-style-panel-model が正本として持ち、
// 配置前パネルと同じ定義から組み立てる（片方だけがずれることを構造的に防ぐ）。
//
// 表示可否はすべて resolveSelectionStyleTarget が判定する（ToolBar 側に条件を重複させない）。

interface SelectionStylePanelProps {
  // ToolBar と同じ state・操作を受け取り、表示の正本を二重に持たない。
  readonly palettes: CustomColorPalettes;
}

export function SelectionStylePanel({ palettes }: SelectionStylePanelProps): JSX.Element | null {
  const tool = useCanvasStore((s) => s.tool);
  const selectedObjectIds = useCanvasStore((s) => s.selectedObjectIds);
  const readOnly = useCanvasStore((s) => s.readOnly);
  // テキスト範囲専用パネルが出ている間は、全文用パネルを描画しない（操作対象を明確にする）。
  const activeTextRangeObjectId = useCanvasStore(
    (s) => s.activeTextRangeSelection?.objectId ?? null
  );
  // 表のセルを編集している間も、同じ理由で表全体のパネルを描画しない（セル書式パネルと排他）。
  const tableCellEditing = useCanvasStore((s) => s.tableCellEditing);
  // ただし補助入力は、極小・低倍率・画面外の表で唯一の入力手段のため、セル編集中も残す。
  const auxiliaryInputActive = useTableCellEditorStore(
    (s) => s.handle !== null && s.auxiliary
  );
  const objects = useCanvasDocStore((s) => s.objects);
  const setPenColor = useCanvasStore((s) => s.setPenColor);
  const setPenStrokePattern = useCanvasStore((s) => s.setPenStrokePattern);
  const setPenThickness = useCanvasStore((s) => s.setPenThickness);
  const setStickyColor = useCanvasStore((s) => s.setStickyColor);
  const setShapeFill = useCanvasStore((s) => s.setShapeFill);
  const setShapeStroke = useCanvasStore((s) => s.setShapeStroke);
  const setShapeStrokePattern = useCanvasStore((s) => s.setShapeStrokePattern);
  const setShapeStrokeWidth = useCanvasStore((s) => s.setShapeStrokeWidth);
  const setSectionFill = useCanvasStore((s) => s.setSectionFill);
  const setTextFontSize = useCanvasStore((s) => s.setTextFontSize);
  const setTextAlign = useCanvasStore((s) => s.setTextAlign);
  const dispatcher = useCanvasOpDispatcherContext();

  // objects は op の確定ごとに参照が変わるため、パネルを出す余地が無い状態では走査自体を行わない
  // （既定の編集状態＝非選択で追加コストを発生させない）。オブジェクトのドラッグ中の座標更新は
  // canvas-transient-store 側で行われ objects は変わらないため、ドラッグ中の再計算も発生しない。
  const target: SelectionStyleTarget | null = useMemo(() => {
    if (tool !== "idle" || selectedObjectIds.length === 0) return null;
    return resolveSelectionStyleTarget(objects, selectedObjectIds, tool);
  }, [objects, selectedObjectIds, tool]);
  // 接続矢印の線の形・両端の形状・経路の編集は、サーバの対応を確認できたときだけ出す。
  const connectorEditingAvailable = useConnectorEditingAvailable();
  // 選択中の接続矢印の手動経路を、図形の配置に合わせて補正して表示しているか（描画と同じ共有の幾何）。
  // 補正は手動経路にしか起きないため、自動経路の接続矢印では経路を求めない（確定のたびの再計算を省く）。
  const connectorRouteAdjusted = useMemo(() => {
    if (target?.kind !== "connector") return false;
    return target.targetIds.some((id) => {
      const connector = objects[id];
      if (connector?.type !== "connector") return false;
      if (resolveConnectorAppearance(connector).route.mode !== "manual") return false;
      const fromObj = objects[connector.from.objectId];
      const toObj = objects[connector.to.objectId];
      if (!fromObj || !toObj) return false;
      return computeConnectorGeometry(
        connectorGeometryInputFor(connector, connectorFrameOf(fromObj), connectorFrameOf(toObj))
      ).adjusted;
    });
  }, [objects, target]);

  // 参照専用ビューでは ToolBar 自体が描画されないが、将来の再利用に備えた防御として抑止する。
  if (readOnly) return null;
  // 範囲選択があるあいだは全文用パネルを出さない。範囲は「編集中のテキスト図形」だけが持ち、
  // その図形の編集が終わる・対象がアンマウントされる時点で解除されるため、対象 ID を照合せずに
  // 抑止しても他の選択対象へ波及しない。
  if (activeTextRangeObjectId !== null) return null;
  // セル編集中は、補助入力を出す必要があるときだけ外枠を残す（それ以外は枠ごと描かない）。
  if (tableCellEditing !== null && !auxiliaryInputActive) return null;
  if (!target) return null;

  const targetIds = target.targetIds;

  // コネクタとテキストの色以外の各ハンドラは常に
  // 「既定値の更新 → 選択中オブジェクトへの適用」の順で両方を無条件に実行する。
  // コネクタの線色は配置前の既定値 UI を持たないため、選択中だけを更新する。
  // テキストの文字色・背景色・太字は配置前パネルにも既定値 UI があるが、そちらで明示した値を
  // 正本とするため、選択操作から配置既定値へは波及させない（文字サイズ・寄せは従来どおり波及）。
  // 文字サイズの子パネルは表とも共有しているため、その波及はテキストを選んでいるときだけに限る
  // （表の文字サイズは表全体の書式であり、次のテキスト配置の既定値とは別物のため）。
  // 描画からクリックまでの間に他参加者が全対象を削除・ロックしても、適用側の既存ガードにより
  // 自動的に no-op になる。既定値の更新は利用者が明示的に選んだ操作であり、共有状態を変えないため、
  // 対象の有無で分岐させない。
  const chooseStickyColor = (color: string): void => {
    setStickyColor(color);
    saveStickyColor(color);
    dispatcher.updateObjectsStyle(targetIds, { color });
  };
  const choosePenColor = (color: string): void => {
    setPenColor(color);
    savePenColor(color);
    dispatcher.updateObjectsStyle(targetIds, { color });
  };
  const choosePenThickness = (thickness: PenThickness): void => {
    setPenThickness(thickness);
    dispatcher.updateObjectsStyle(targetIds, { thickness: PEN_THICKNESS_PIXELS[thickness] });
  };
  const choosePenStrokePattern = (strokePattern: StrokePattern): void => {
    setPenStrokePattern(strokePattern);
    dispatcher.updateObjectsStyle(targetIds, { strokePattern });
  };
  const chooseSectionFill = (fill: string): void => {
    setSectionFill(fill);
    dispatcher.updateObjectsStyle(targetIds, { fill });
  };
  const chooseShapeFill = (fill: string): void => {
    // 図形の背景色だけは、op へ送る値（"transparent"）と既定値として保存する値（"none"）が異なる。
    const preferenceValue = fill === SHAPE_FILL_TRANSPARENT ? SHAPE_FILL_NONE : fill;
    setShapeFill(preferenceValue);
    saveShapeFill(preferenceValue);
    dispatcher.updateObjectsStyle(targetIds, { fill });
  };
  const chooseShapeStroke = (stroke: string): void => {
    // 枠線色も背景色と同様、選好では none、op では transparent を使い分ける。
    const preferenceValue = stroke === SHAPE_STROKE_TRANSPARENT ? SHAPE_STROKE_NONE : stroke;
    setShapeStroke(preferenceValue);
    saveShapeStroke(preferenceValue);
    dispatcher.updateObjectsStyle(targetIds, { stroke });
  };
  const chooseShapeStrokeWidth = (width: ShapeStrokeWidth): void => {
    setShapeStrokeWidth(width);
    dispatcher.updateObjectsStyle(targetIds, {
      strokeWidth: SHAPE_STROKE_WIDTH_PIXELS[width],
    });
  };
  const chooseShapeStrokePattern = (strokePattern: StrokePattern): void => {
    setShapeStrokePattern(strokePattern);
    dispatcher.updateObjectsStyle(targetIds, { strokePattern });
  };
  const chooseConnectorStroke = (stroke: string): void => {
    dispatcher.updateObjectsStyle(targetIds, { stroke });
  };
  const chooseConnectorStrokeWidth = (width: ShapeStrokeWidth): void => {
    dispatcher.updateObjectsStyle(targetIds, {
      strokeWidth: CONNECTOR_STROKE_WIDTH_PIXELS[width],
    });
  };
  // 線の形・線のパターン・両端の形状・経路は、選択中の接続矢印すべてへ 1 回の履歴で適用する。
  // 直線は手動経路を持てないため、直線への切り替えは同じ操作で経路を自動へ戻す（1 回の取り消しで
  // 元の線の形と経路へ戻る）。
  const chooseConnectorLineType = (lineType: ConnectorLineType): void => {
    dispatcher.updateConnectors(
      targetIds,
      lineType === "straight" ? { lineType, route: CONNECTOR_AUTO_ROUTE } : { lineType }
    );
  };
  const chooseConnectorLinePattern = (linePattern: ConnectorLinePattern): void => {
    dispatcher.updateConnectors(targetIds, { linePattern });
  };
  const chooseConnectorStartCap = (startCap: ConnectorCap): void => {
    dispatcher.updateConnectors(targetIds, { startCap });
  };
  const chooseConnectorEndCap = (endCap: ConnectorCap): void => {
    dispatcher.updateConnectors(targetIds, { endCap });
  };
  const resetConnectorRoutes = (): void => {
    dispatcher.updateConnectors(targetIds, { route: CONNECTOR_AUTO_ROUTE });
  };
  // 文字サイズ・寄せの setter は保存失敗時に配置既定値を直前値へ戻すが、ここでは利用者の
  // 操作（選択中オブジェクトへの適用）自体は成功しているため、戻り値は使わず通知もしない。
  const chooseTextFontSize = (px: number): void => {
    // 配置既定値（次に置くテキスト図形の文字サイズ。localStorage へ永続化する）を更新するのは、
    // テキスト図形を選んでいるときだけにする。この子パネルは表とも共有しており、表の文字サイズは
    // 表全体の書式であって次のテキスト配置とは無関係なため、表の操作で既定値を書き換えない。
    // 文字寄せは表のパネルへ出していないため、chooseTextAlign にはこの分岐が要らない。
    if (target.kind === "text") setTextFontSize(px);
    dispatcher.updateObjectsStyle(targetIds, { fontSize: px });
  };
  const chooseTextAlign = (align: TextAlign): void => {
    setTextAlign(align);
    dispatcher.updateObjectsStyle(targetIds, { textAlign: align });
  };
  // テキストの文字色・背景色は、配置前パネルで明示した値を正本とするため、
  // 選択操作からは次の新規配置の既定値へ記憶しない（太字も同じ扱い）。
  const chooseTextColor = (color: string): void => {
    dispatcher.updateObjectsStyle(targetIds, { color });
  };
  const chooseTextFill = (fill: string): void => {
    dispatcher.updateObjectsStyle(targetIds, { fill });
  };

  // 「＋」のカラーピッカーは無効な値を返し得るため、既存の判定で弾いてから正規化する。
  // 追加後は色見本を選んだのと同じ経路へ流す。テキストの色ではパレット追加と適用だけを行い、
  // その他ではパレット追加・既定値更新・適用を対にする。
  const addThenChoose = (
    value: string,
    addToPalette: (color: string) => void,
    choose: (color: string) => void
  ): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    addToPalette(normalized);
    choose(normalized);
  };

  // テキストの子パネルは、配置前パネルと同じ共有モデルへ委譲する。
  // 操作対象だけが違い、項目・順序・選択肢・見出しは 1 か所（text-style-panel-model）で決まる。
  const textChildInput: TextStyleChildInput = {
    testIdPrefix: "selection-style",
    ariaLabelPrefix: "選択中のテキストの",
    palettes,
    color: target.common.color,
    fill: target.common.fill,
    textAlign: target.common.textAlign,
    fontSize: target.common.fontSize,
    onChooseColor: chooseTextColor,
    onChooseFill: chooseTextFill,
    onChooseAlign: chooseTextAlign,
    onChooseFontSize: chooseTextFontSize,
  };

  const renderPanels = (activeChild: SelectionStyleChildKey | null): JSX.Element => {
    switch (target.kind) {
      case "sticky":
        return (
          <>
            {activeChild === "sticky-color" ? (
              <SubPanel label="付箋の色" testId="selection-style-sticky-color">
                <ColorSwatchList
                  ariaLabel="選択中の付箋の色"
                  presetColors={STICKY_PRESET_COLORS}
                  customColors={palettes.sticky.colors}
                  value={target.common.color}
                  onChoose={chooseStickyColor}
                  onAddColor={(value) =>
                    addThenChoose(value, palettes.sticky.add, chooseStickyColor)
                  }
                  onUpdateCustomColor={palettes.sticky.update}
                  onDeleteCustomColor={palettes.sticky.remove}
                />
              </SubPanel>
            ) : null}
            {/* 付箋の文字寄せもテキストと同じ子パネルを使い、選択肢の差を作らない。 */}
            {activeChild === "text-align"
              ? renderTextStyleChild("text-align", textChildInput)
              : null}
          </>
        );
      case "section":
        return (
          <SubPanel label="背景色" testId="selection-style-section-fill">
            <ColorSwatchList
              ariaLabel="選択中のセクションの背景色"
              presetColors={SECTION_FILL_PRESET_COLORS}
              customColors={palettes.section.colors}
              value={target.common.fill}
              onChoose={chooseSectionFill}
              onAddColor={(value) => addThenChoose(value, palettes.section.add, chooseSectionFill)}
              onUpdateCustomColor={palettes.section.update}
              onDeleteCustomColor={palettes.section.remove}
            />
          </SubPanel>
        );
      case "shape":
        return (
          <>
            {target.shapeKindGroup === "fillable" && activeChild === "shape-fill" ? (
              <SubPanel label="背景色" testId="selection-style-shape-fill">
                <ColorSwatchList
                  ariaLabel="選択中の図形の背景色"
                  presetColors={SHAPE_FILL_PRESET_COLORS}
                  customColors={palettes.shape.colors}
                  value={target.common.fill}
                  onChoose={chooseShapeFill}
                  onAddColor={(value) => addThenChoose(value, palettes.shape.add, chooseShapeFill)}
                  onUpdateCustomColor={palettes.shape.update}
                  onDeleteCustomColor={palettes.shape.remove}
                  noneOption={{ value: SHAPE_FILL_TRANSPARENT, label: "背景なし" }}
                />
              </SubPanel>
            ) : null}
            {activeChild === "shape-stroke" ? (
              <SubPanel
                label={target.shapeKindGroup === "fillable" ? "枠線の色" : "線の色"}
                testId="selection-style-shape-stroke"
              >
                <ColorSwatchList
                  ariaLabel={
                    target.shapeKindGroup === "fillable"
                      ? "選択中の図形の枠線の色"
                      : "選択中の矢印の線の色"
                  }
                  presetColors={SHAPE_STROKE_PRESET_COLORS}
                  customColors={palettes.shapeStroke.colors}
                  value={target.common.stroke}
                  onChoose={chooseShapeStroke}
                  onAddColor={(value) =>
                    addThenChoose(value, palettes.shapeStroke.add, chooseShapeStroke)
                  }
                  onUpdateCustomColor={palettes.shapeStroke.update}
                  onDeleteCustomColor={palettes.shapeStroke.remove}
                  noneOption={
                    target.shapeKindGroup === "fillable"
                      ? { value: SHAPE_STROKE_TRANSPARENT, label: "線なし" }
                      : undefined
                  }
                />
              </SubPanel>
            ) : null}
            {activeChild === "shape-stroke-width" ? (
              <SubPanel
                label={target.shapeKindGroup === "fillable" ? "枠線の太さ" : "線の太さ"}
                testId="selection-style-shape-stroke-width"
              >
                <ShapeStrokeWidthChooser
                  value={target.common.strokeWidth}
                  color={target.common.stroke ?? ""}
                  ariaLabelPrefix={target.shapeKindGroup === "fillable" ? "枠線の太さ" : "線の太さ"}
                  onChange={chooseShapeStrokeWidth}
                />
              </SubPanel>
            ) : null}
            {activeChild === "shape-stroke-pattern" ? (
              <SubPanel
                label={target.shapeKindGroup === "fillable" ? "枠線の種類" : "線の種類"}
                testId="selection-style-shape-stroke-pattern"
              >
                <StrokePatternChooser
                  value={target.common.strokePattern}
                  onChange={chooseShapeStrokePattern}
                />
              </SubPanel>
            ) : null}
          </>
        );
      case "connector":
        return (
          <>
            {activeChild === "connector-stroke" ? (
              <SubPanel label="線の色" testId="selection-style-connector-stroke">
                <ColorSwatchList
                  ariaLabel="選択中の接続矢印の線の色"
                  presetColors={CONNECTOR_STROKE_PRESET_COLORS}
                  customColors={palettes.shapeStroke.colors}
                  value={target.common.stroke}
                  onChoose={chooseConnectorStroke}
                  onAddColor={(value) =>
                    addThenChoose(value, palettes.shapeStroke.add, chooseConnectorStroke)
                  }
                  onUpdateCustomColor={palettes.shapeStroke.update}
                  onDeleteCustomColor={palettes.shapeStroke.remove}
                />
              </SubPanel>
            ) : null}
            {activeChild === "connector-stroke-width" ? (
              <SubPanel label="線の太さ" testId="selection-style-connector-stroke-width">
                <ShapeStrokeWidthChooser
                  value={target.common.strokeWidth}
                  color={target.common.stroke ?? ""}
                  ariaLabelPrefix="線の太さ"
                  onChange={chooseConnectorStrokeWidth}
                />
              </SubPanel>
            ) : null}
            {activeChild === "connector-line-type" ? (
              <SubPanel label="線の形" testId="selection-style-connector-line-type">
                <ConnectorLineTypeChooser
                  value={target.common.connectorLineType}
                  onChange={chooseConnectorLineType}
                />
              </SubPanel>
            ) : null}
            {activeChild === "connector-line-pattern" ? (
              <SubPanel label="線の種類" testId="selection-style-connector-line-pattern">
                <ConnectorLinePatternChooser
                  value={target.common.connectorLinePattern}
                  onChange={chooseConnectorLinePattern}
                />
              </SubPanel>
            ) : null}
            {activeChild === "connector-start-cap" ? (
              <SubPanel label="始点" testId="selection-style-connector-start-cap">
                <ConnectorCapChooser
                  end="start"
                  value={target.common.connectorStartCap}
                  onChange={chooseConnectorStartCap}
                />
              </SubPanel>
            ) : null}
            {activeChild === "connector-end-cap" ? (
              <SubPanel label="終点" testId="selection-style-connector-end-cap">
                <ConnectorCapChooser
                  end="end"
                  value={target.common.connectorEndCap}
                  onChange={chooseConnectorEndCap}
                />
              </SubPanel>
            ) : null}
          </>
        );
      case "text":
        // 4 項目すべて共有モデルが描く。項目キー以外の子は、この種別では開かない。
        return activeChild !== null && isTextStyleItemKey(activeChild) ? (
          renderTextStyleChild(activeChild, textChildInput)
        ) : (
          <></>
        );
      case "table":
        // 表では文字色・文字サイズだけを変更できる。共有のテキスト書式部品を使い、
        // 背景色・文字寄せ・太字は表のスタイルとして公開しない。
        return activeChild === "text-color" || activeChild === "text-size" ? (
          renderTextStyleChild(activeChild, {
            ...textChildInput,
            ariaLabelPrefix: "選択中の表の",
          })
        ) : (
          <></>
        );
      case "pen-stroke":
        return (
          <>
            {activeChild === "pen-color" ? (
              <SubPanel label="ペンの色" testId="selection-style-pen-color">
                <ColorSwatchList
                  ariaLabel="選択中のペンの色"
                  presetColors={PEN_PRESET_COLORS}
                  customColors={palettes.pen.colors}
                  value={target.common.color}
                  onChoose={choosePenColor}
                  onAddColor={(value) => addThenChoose(value, palettes.pen.add, choosePenColor)}
                  onUpdateCustomColor={palettes.pen.update}
                  onDeleteCustomColor={palettes.pen.remove}
                />
              </SubPanel>
            ) : null}
            {activeChild === "pen-thickness" ? (
              <SubPanel label="太さ" testId="selection-style-pen-thickness">
                {/* 太さプレビューのドット色は、色が混在している場合だけ空文字を渡して
                既定の文字色（var(--color-text)）へフォールバックさせる。 */}
                <PenThicknessChooser
                  value={target.common.thickness}
                  color={target.common.color ?? ""}
                  onChange={choosePenThickness}
                />
              </SubPanel>
            ) : null}
            {activeChild === "pen-stroke-pattern" ? (
              <SubPanel label="線の種類" testId="selection-style-pen-stroke-pattern">
                <StrokePatternChooser
                  value={target.common.strokePattern}
                  onChange={choosePenStrokePattern}
                />
              </SubPanel>
            ) : null}
          </>
        );
    }
  };

  const item = (
    key: SelectionStyleChildKey,
    label: string,
    value: string | number | null,
    preview: ReactNode
  ): SelectionStyleParentItem => ({ key, label, mixed: value === null, preview });
  const colorPreview = (value: string | null): ReactNode => <ColorValuePreview value={value} />;
  const linePreview = (width: number | null): ReactNode =>
    width === null ? (
      "—"
    ) : (
      <span
        className="canvas-selection-style-line"
        style={{ height: `${Math.min(width, 16)}px` }}
      />
    );
  const colorItem = (
    key: SelectionStyleChildKey,
    label: string,
    value: string | null
  ): SelectionStyleParentItem => item(key, label, value, colorPreview(value));
  // 付箋の文字寄せは、テキストと同じラベル・同じプレビューを共有モデルから受け取る。
  const alignItem = item(
    "text-align",
    TEXT_STYLE_ITEM_LABELS["text-align"],
    target.common.textAlign,
    textStyleAlignPreview(target.common.textAlign)
  );
  // 行・列の増減は「いまの表の形」を必要とするため、単独選択の表だけを操作対象にする。
  // 複数選択では「セルを編集」だけを無効化し、増減は先頭の表の形を基準に行う。
  const tableTarget =
    target.kind === "table"
      ? ((targetIds
          .map((id) => objects[id])
          .find((object) => object?.type === "table") as TableCanvasObject | undefined) ?? null)
      : null;
  const width =
    target.common.strokeWidth === null
      ? null
      : SHAPE_STROKE_WIDTH_PIXELS[target.common.strokeWidth];
  const penWidth =
    target.common.thickness === null ? null : PEN_THICKNESS_PIXELS[target.common.thickness];
  const items: readonly SelectionStyleParentItem[] =
    target.kind === "text"
      ? // 項目・順序・プレビューは配置前パネルと同じ共有モデルから受け取る。
        textStyleParentItems({
          color: target.common.color,
          fill: target.common.fill,
          textAlign: target.common.textAlign,
          fontSize: target.common.fontSize,
        })
      : target.kind === "table"
        ? textStyleParentItems({
            color: target.common.color,
            fill: null,
            textAlign: null,
            fontSize: target.common.fontSize,
          }).filter((entry) => entry.key === "text-color" || entry.key === "text-size")
        : target.kind === "sticky"
          ? [colorItem("sticky-color", "付箋の色", target.common.color), alignItem]
          : target.kind === "shape"
            ? [
                ...(target.shapeKindGroup === "fillable"
                  ? [colorItem("shape-fill", "背景色", target.common.fill)]
                  : []),
                colorItem(
                  "shape-stroke",
                  target.shapeKindGroup === "fillable" ? "枠線の色" : "線の色",
                  target.common.stroke
                ),
                item(
                  "shape-stroke-pattern",
                  target.shapeKindGroup === "fillable" ? "枠線の種類" : "線の種類",
                  target.common.strokePattern,
                  target.common.strokePattern === null ? (
                    "—"
                  ) : (
                    <StrokePatternPreview pattern={target.common.strokePattern} />
                  )
                ),
                item(
                  "shape-stroke-width",
                  target.shapeKindGroup === "fillable" ? "枠線の太さ" : "線の太さ",
                  width,
                  linePreview(width)
                ),
              ]
            : target.kind === "connector"
              ? [
                  colorItem("connector-stroke", "線の色", target.common.stroke),
                  item("connector-stroke-width", "線の太さ", width, linePreview(width)),
                  // 線の形・線のパターン・両端の形状は、サーバの対応を確認できたときだけ出す
                  // （色・太さは従来の更新で、旧サーバも理解する）。
                  ...(connectorEditingAvailable
                    ? [
                        item(
                          "connector-line-type",
                          "線の形",
                          target.common.connectorLineType,
                          target.common.connectorLineType === null ? (
                            "—"
                          ) : (
                            <ConnectorLineTypePreview lineType={target.common.connectorLineType} />
                          )
                        ),
                        item(
                          "connector-line-pattern",
                          "線の種類",
                          target.common.connectorLinePattern,
                          target.common.connectorLinePattern === null ? (
                            "—"
                          ) : (
                            <ConnectorLinePatternPreview
                              pattern={target.common.connectorLinePattern}
                            />
                          )
                        ),
                        item(
                          "connector-start-cap",
                          "始点",
                          target.common.connectorStartCap,
                          target.common.connectorStartCap === null ? (
                            "—"
                          ) : (
                            <ConnectorCapPreview cap={target.common.connectorStartCap} end="start" />
                          )
                        ),
                        item(
                          "connector-end-cap",
                          "終点",
                          target.common.connectorEndCap,
                          target.common.connectorEndCap === null ? (
                            "—"
                          ) : (
                            <ConnectorCapPreview cap={target.common.connectorEndCap} end="end" />
                          )
                        ),
                      ]
                    : []),
                ]
              : target.kind === "pen-stroke"
                ? [
                    colorItem("pen-color", "ペンの色", target.common.color),
                    item(
                      "pen-stroke-pattern",
                      "線の種類",
                      target.common.strokePattern,
                      target.common.strokePattern === null ? (
                        "—"
                      ) : (
                        <StrokePatternPreview pattern={target.common.strokePattern} />
                      )
                    ),
                    item("pen-thickness", "太さ", penWidth, linePreview(penWidth)),
                  ]
                : [];
  // 選択 ID の順序や値更新では開状態を保ち、種別・構成・選択集合の変更時だけ初期化する。
  const contextKey = JSON.stringify([
    target.kind,
    target.shapeKindGroup,
    [...selectedObjectIds].sort(),
  ]);
  const kindLabel = {
    sticky: "付箋",
    shape: "図形",
    connector: "接続矢印",
    text: "テキスト",
    table: "表",
    "pen-stroke": "ペン",
    section: "セクション",
  }[target.kind];
  return (
    <SelectionStylePanelFrame>
      {tableCellEditing !== null ? null : target.kind === "section" ? (
        renderPanels(null)
      ) : (
        <SelectionStyleParentPanel
          key={contextKey}
          label={`選択中の${kindLabel}のスタイル`}
          items={items}
          renderChild={renderPanels}
          bold={target.common.bold}
          onToggleBold={
            target.kind === "text"
              ? () =>
                  dispatcher.updateObjectsStyle(targetIds, { bold: target.common.bold !== true })
              : undefined
          }
          extraActions={
            // 表では、セル編集の入口と行・列の増減を子パネルを開かない直接操作として並べる。
            tableTarget ? (
              <TableShapeActions
                table={tableTarget}
                singleSelection={targetIds.length === 1}
                dispatcher={dispatcher}
              />
            ) : target.kind === "connector" && connectorEditingAvailable ? (
              <ConnectorRouteActions
                hasManualRoute={target.common.connectorHasManualRoute}
                adjusted={connectorRouteAdjusted}
                onResetRoute={resetConnectorRoutes}
              />
            ) : undefined
          }
        />
      )}
      {/* インライン入力が使えない表向けの補助入力欄。編集していないときは何も描かない。 */}
      <TableCellAuxiliaryInput />
    </SelectionStylePanelFrame>
  );
}
